import leftArrow from '@/icons/carousel-arrow-left-rebrand.svg';
import rightArrow from '@/icons/carousel-arrow-right-rebrand.svg';
import {createShopifyImageUrl} from '@/utilities/create-shopify-image-url';
import {preloadImage} from '@/utilities/preload-image';
import Slider, {CustomArrowProps} from '@ant-design/react-slick';
import classNames from 'classnames';
import {useEffect, useRef, useState} from 'preact/hooks';
import 'slick-carousel/slick/slick-theme.css';
import 'slick-carousel/slick/slick.css';
import CarouselImagePreviewSquare from '../CarouselImagePreviewSquare';
import {logInfo} from '@/utilities/log';

export default ProductImageCarousel;

/**
 * Renders a carousel component for displaying product images.
 *
 * @param customPaging - A custom paging component to display in the carousel. If not provided, the default image preview component is used.
 * @param images - An array of product images.
 * @param featured - The featured image to display when no active image is selected.
 * @param showArrows - Whether to display the arrow buttons for navigating the carousel.
 * @param showPagingOnMobile - Whether to display the paging dots on mobile devices.
 * @deprecated Use `ProductImageCarousel` from `@/components/buy-stack/full-width/ProductImageCarousel` instead.
 *
 * @example
 * ```tsx
 * <ProductImageCarousel
 *  images={images}
 *  featured={featured}
 *  showArrows={false}
 *  showPagingOnMobile={true}
 *  customPaging={({i, image, isActive}) => (
 *   <MyCustomPagingComponent
 *    image={image}
 *    isActive={isActive}
 *    onClick={() => console.log('Clicked on image', i)}
 *  />
 * )}
 * />
 * ```
 */
function ProductImageCarousel({
  customPaging,
  images,
  featured,
  showArrows = true,
  showPagingOnMobile = false,
}: ProductImageCarouselProps) {
  const [active, setActive] = useState(0);
  const mainImage = images[active] ?? featured;

  const slider = useRef<Slider | null>(null);

  useEffect(() => {
    // Force update the carousel when the images change
    // This is a workaround to prevent the carousel from breaking
    // TODO: Investigate a better solution
    slider.current?.forceUpdate();
    slider.current?.slickGoTo(active);

    // Preload each image in large size when the component mounts
    // so they are displayed instantly when the user switches between them
    images.forEach((image) =>
      preloadImage(createShopifyImageUrl(image.url, 640))
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [images]);

  return (
    <div
      className={classNames('relative  px-8', {
        'md:pb-20': images.length <= 1,
        'max-h-[clamp(150px,calc(100vh-38rem),250px)] overflow-hidden md:max-h-unset':
          !showPagingOnMobile,
      })}
    >
      <Slider
        ref={slider}
        dots={true}
        dotsClass=""
        fade={true}
        easing="ease-in-out"
        infinite={true}
        speed={300}
        arrows={showArrows}
        appendDots={(dots) => (
          <ul
            className={classNames('mt-6 flex list-none justify-center gap-x-3')}
          >
            {dots}
          </ul>
        )}
        beforeChange={(_, newIndex) => {
          logInfo('ChangeCarouselImage', {
            index: newIndex,
            url: images[newIndex]?.url,
          });
          setActive(newIndex);
        }}
        // A custom paging component can be injected via props
        // Or the default image preview component is used
        customPaging={(i) => {
          const image = images[i];
          const isActive = i === active;
          return customPaging ? (
            customPaging({i, image, isActive})
          ) : (
            <div
              className={classNames({
                'hidden md:block': !showPagingOnMobile,
              })}
            >
              <CarouselImagePreviewSquare
                image={image}
                isActive={isActive}
                onClick={() =>
                  logInfo('ClickCarouselPreviewImage', {
                    index: i,
                    url: image.url,
                  })
                }
              />
            </div>
          );
        }}
        nextArrow={
          <Arrow
            image={{url: rightArrow, alt: 'Right arrow icon'}}
            buttonClassName={classNames(
              'absolute top-1/2 md:translate-y-[calc(-50%-3rem)] -right-6 h-8 w-5',
              {
                '-translate-y-1/2': !showPagingOnMobile,
                'translate-y-[calc(-50%-3rem)]': showPagingOnMobile,
              }
            )}
          />
        }
        prevArrow={
          <Arrow
            image={{url: leftArrow, alt: 'Left arrow icon'}}
            buttonClassName={classNames(
              'absolute top-1/2 -left-6 md:translate-y-[calc(-50%-3rem)] h-8 w-5',
              {
                '-translate-y-1/2': !showPagingOnMobile,
                'translate-y-[calc(-50%-3rem)]': showPagingOnMobile,
              }
            )}
          />
        }
      >
        {images.map((image) => (
          <div key={image.url}>
            <img
              src={createShopifyImageUrl(mainImage.url, 640)}
              alt={mainImage.alt ?? ''}
              // eslint-disable-next-line tailwindcss/no-custom-classname
              className="pointer-events-none max-h-[clamp(150px,calc(100vh-38rem),250px)] w-full object-contain md:max-h-unset"
            />
          </div>
        ))}
      </Slider>
    </div>
  );
}

/**
 * Renders a custom arrow component with an image.
 * This is used to override the default buttons in the carousel.
 *
 * @param image - The image object containing the URL and alt text.
 * @param style - The inline style for the arrow component.
 * @returns The rendered arrow component.
 */
function Arrow({image, buttonClassName, onClick}: ArrowProps) {
  return (
    <button
      className={buttonClassName}
      onClick={onClick as (_: MouseEvent) => void}
    >
      <img
        src={image.url}
        alt={image.alt ?? ''}
        className="size-full object-contain"
      />
    </button>
  );
}

export type CustomPagingProps = {
  i: number;
  image: Image;
  isActive: boolean;
};

export type ProductImageCarouselProps = {
  customPaging?: (props: CustomPagingProps) => JSX.Element;
  featured: Image;
  images: Image[];
  showArrows?: boolean;
  showPagingOnMobile?: boolean;
};

type ArrowProps = CustomArrowProps & {
  image: Image;
  buttonClassName: string;
};

type Image = {
  alt: string | null;
  url: string;
};
